import React from 'react';
import type { DiscountSource, PromotionApplied } from '../../types/appointment';
import { formatCurrency } from '../../utils/formatters';

interface DiscountBadgeProps {
  discountSource?: DiscountSource;
  promotionApplied?: PromotionApplied;
  originalPrice?: number;
  finalPrice?: number;
  /** When true (client view), show "You saved $X!" text */
  showSavings?: boolean;
  size?: 'sm' | 'md';
}

const DiscountBadge: React.FC<DiscountBadgeProps> = ({
  discountSource,
  promotionApplied,
  originalPrice,
  finalPrice,
  showSavings = false,
  size = 'md',
}) => {
  if (!discountSource || discountSource === 'none') return null;

  const savedAmount =
    typeof originalPrice === 'number' && typeof finalPrice === 'number'
      ? originalPrice - finalPrice
      : promotionApplied?.discountAmount;

  const isSmall = size === 'sm';
  const badgePad = isSmall ? '2px 6px' : '3px 10px';
  const badgeFont = isSmall ? '11px' : '12px';
  const gap = isSmall ? '4px' : '6px';

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap }}>
      {(discountSource === 'promotion' || discountSource === 'both') && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: 'linear-gradient(135deg, #C9A84C, #A8872A)',
            color: '#fff',
            borderRadius: '12px',
            padding: badgePad,
            fontSize: badgeFont,
            fontWeight: 600,
          }}
        >
          🎁 {promotionApplied?.title || 'Promo'}
          {promotionApplied?.discountType === 'percentage'
            ? ` (${promotionApplied.discountValue}% off)`
            : promotionApplied?.discountAmount
            ? ` (-${formatCurrency(promotionApplied.discountAmount)})`
            : ''}
        </span>
      )}

      {(discountSource === 'loyalty' || discountSource === 'both') && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: 'linear-gradient(135deg, #F59E0B, #FCD34D)',
            color: '#1a1a1a',
            borderRadius: '12px',
            padding: badgePad,
            fontSize: badgeFont,
            fontWeight: 600,
          }}
        >
          ⭐ Points Redeemed
        </span>
      )}

      {showSavings && savedAmount !== undefined && savedAmount > 0 && (
        <span
          style={{
            fontSize: badgeFont,
            color: '#16a34a',
            fontWeight: 600,
          }}
        >
          You saved {formatCurrency(savedAmount)}!
        </span>
      )}
    </div>
  );
};

export default DiscountBadge;
